import { useCallback, useState } from 'react';
import type { SpeciesId } from '../../types.ts';
import { totalEvs } from '../../util/stat-math.ts';
import { useIsMobile } from '../../util/useIsMobile.ts';
import { Sprite } from '../Sprite.tsx';
import { type BuilderSpecies } from './builder-data.ts';
import { ContextRail } from './ContextRail.tsx';
import { Editor } from './Editor.tsx';
import { SpeciesPicker } from './SpeciesPicker.tsx';
import { type EditorMember, serializeTeam } from './serialize.ts';

const MAX_TEAM = 6;

// Top-level builder page. Three-column layout matching the design handoff:
// team list (left) · Editor (center) · context rail (right, deferred to a
// future slice — for now an empty placeholder).
//
// State lives here (a single useState for the team array + activeSlot).
// Per curmudgeon: no global store for one page. The Editor receives the
// active slot's member and an onChange callback; the SpeciesPicker overlay
// is mounted at this level too because it's also used for empty-slot
// instantiation.

export function Builder({
  regulationId = 'M-A',
  initialTeam,
  initialName,
  onAttach,
}: {
  regulationId?: string;
  // Optional seed (e.g. when navigating to /build with an existing team
  // loaded from elsewhere). Defaults to 6 empty slots.
  initialTeam?: Array<EditorMember | null>;
  initialName?: string;
  // When provided, an "attach to chat" button appears next to Save. The
  // chat surface passes the current team back as a draft attachment on the
  // composer; closes the modal once attached. Partial teams (1-6 mons) are
  // valid — full validation happens server-side when the chat is sent.
  onAttach?: (members: Array<EditorMember | null>) => void;
}) {
  const [team, setTeam] = useState<Array<EditorMember | null>>(() => {
    if (initialTeam) {
      const padded = [...initialTeam];
      while (padded.length < MAX_TEAM) padded.push(null);
      return padded.slice(0, MAX_TEAM);
    }
    return Array.from({ length: MAX_TEAM }, () => null);
  });
  const [activeSlot, setActiveSlot] = useState<number>(0);
  const [teamName, setTeamName] = useState(initialName ?? '');
  const [pickerForSlot, setPickerForSlot] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<{ id: number; name: string } | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  // Mobile: stack the layout, hide the rail by default, expose a toggle.
  const isMobile = useIsMobile();
  const [railOpen, setRailOpen] = useState(false);

  const memberCount = team.filter((m) => m !== null).length;

  const updateSlot = useCallback((i: number, next: EditorMember | null) => {
    setTeam((prev) => {
      const copy = [...prev];
      copy[i] = next;
      return copy;
    });
    setSaved(null);
  }, []);

  const onPickSpecies = (i: number, species: BuilderSpecies) => {
    setTeam((prev) => {
      const copy = [...prev];
      const existing = copy[i];
      if (existing) {
        // Swap: keep most fields, reset ability to a legal one for the new
        // species (move legality across species is not enforced here — the
        // user can re-check moves manually; matches Showdown's behavior).
        copy[i] = {
          ...existing,
          species: species.id,
          ability: species.abilities[0] ?? existing.ability,
        };
      } else {
        // Empty slot → fresh mon with sensible defaults.
        copy[i] = {
          species: species.id,
          ability: species.abilities[0],
          nature: 'Hardy',
          evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
          ivs: { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
          moves: ['', '', '', ''],
        };
      }
      return copy;
    });
    setActiveSlot(i);
    setPickerForSlot(null);
  };

  async function onSave() {
    const members = team.filter((m): m is EditorMember => m !== null);
    if (members.length === 0) {
      setSaveErr('add at least one Pokemon');
      return;
    }
    const exportText = serializeTeam(members);
    setSaving(true);
    setSaveErr(null);
    try {
      const res = await fetch('/api/teams', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          showdown_export: exportText,
          regulation_id: regulationId,
          name: teamName || derivedName(members),
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        team?: { id: number; name: string };
      };
      if (!res.ok || body.ok === false) {
        setSaveErr(
          body.error === 'unauthorized' ? 'sign in to save' : (body.error ?? 'save failed'),
        );
      } else if (body.team) {
        setSaved({ id: body.team.id, name: body.team.name });
      }
    } catch {
      setSaveErr('network error');
    } finally {
      setSaving(false);
    }
  }

  const activeMember = team[activeSlot];

  return (
    <div
      style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: isMobile ? '12px 10px 96px' : '16px 18px 96px',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 10 : 14,
      }}
    >
      {/* Toolbar */}
      <div
        className="vb-card"
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <span
          className="caps mono"
          style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: '0.08em' }}
        >
          team builder
        </span>
        <input
          type="text"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          placeholder="untitled team"
          className="mono"
          style={{
            flex: 1,
            minWidth: 180,
            // iOS auto-zooms inputs with font-size < 16px. Bump on mobile.
            fontSize: isMobile ? 16 : 13,
            padding: '5px 8px',
            background: 'var(--bg-2)',
            color: 'var(--fg-1)',
            border: '1px solid var(--line-1)',
            borderRadius: 3,
            outline: 'none',
          }}
        />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {memberCount}/{MAX_TEAM} mons
        </span>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || memberCount === 0}
          className="caps mono"
          style={{
            fontSize: 10,
            padding: '5px 12px',
            background: saved ? 'var(--accent-bg)' : 'var(--accent-1)',
            color: saved ? 'var(--accent-2)' : 'var(--bg-0)',
            border: `1px solid var(--accent-line)`,
            borderRadius: 3,
            cursor: saving || memberCount === 0 ? 'not-allowed' : 'pointer',
            opacity: memberCount === 0 ? 0.45 : 1,
            letterSpacing: '0.08em',
          }}
        >
          {saved ? 'saved' : saving ? 'saving…' : 'save'}
        </button>
        {onAttach && (
          <button
            type="button"
            onClick={() => onAttach(team)}
            disabled={memberCount === 0}
            className="caps mono"
            title="Attach this team to your next chat message"
            style={{
              fontSize: 10,
              padding: '5px 12px',
              background: 'var(--accent-bg)',
              color: 'var(--accent-2)',
              border: '1px solid var(--accent-line)',
              borderRadius: 3,
              cursor: memberCount === 0 ? 'not-allowed' : 'pointer',
              opacity: memberCount === 0 ? 0.45 : 1,
              letterSpacing: '0.08em',
            }}
          >
            attach to chat
          </button>
        )}
        {saveErr && (
          <span className="mono" style={{ color: 'var(--neg)', fontSize: 10.5 }}>
            {saveErr}
          </span>
        )}
      </div>

      {isMobile ? (
        // Mobile: stacked. Team list collapses to a horizontal chip strip,
        // editor + rail stack below. Rail starts collapsed (toggle in toolbar).
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <MobileTeamStrip
            team={team}
            activeSlot={activeSlot}
            onSelect={setActiveSlot}
            onPick={(i) => setPickerForSlot(i)}
            onClear={(i) => updateSlot(i, null)}
          />
          <div style={{ minWidth: 0 }}>
            {activeMember ? (
              <Editor
                member={activeMember}
                onChange={(next) => updateSlot(activeSlot, next)}
                onChangeSpecies={() => setPickerForSlot(activeSlot)}
                onClear={() => updateSlot(activeSlot, null)}
                slotLabel={`slot ${activeSlot + 1}`}
                regulationId={regulationId}
              />
            ) : (
              <EmptySlot slot={activeSlot} onPick={() => setPickerForSlot(activeSlot)} />
            )}
          </div>
          <CollapsibleSection
            label={`damage vs top 5 + speed tier${railOpen ? '' : ' · tap to show'}`}
            open={railOpen}
            onToggle={() => setRailOpen((v) => !v)}
          >
            <ContextRail member={activeMember} regulationId={regulationId} />
          </CollapsibleSection>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(200px, 220px) 1fr minmax(280px, 340px)',
            gap: 14,
            alignItems: 'flex-start',
          }}
        >
          {/* Team list */}
          <div className="vb-card" style={{ padding: 6 }}>
            {team.map((m, i) => (
              <TeamSlot
                // biome-ignore lint/suspicious/noArrayIndexKey: slot index IS the identity
                key={i}
                slot={i}
                member={m}
                active={i === activeSlot}
                onSelect={() => setActiveSlot(i)}
                onClear={() => updateSlot(i, null)}
                onPick={() => setPickerForSlot(i)}
              />
            ))}
          </div>

          {/* Editor or empty-state for the active slot */}
          <div style={{ minWidth: 0 }}>
            {activeMember ? (
              <Editor
                member={activeMember}
                onChange={(next) => updateSlot(activeSlot, next)}
                onChangeSpecies={() => setPickerForSlot(activeSlot)}
                onClear={() => updateSlot(activeSlot, null)}
                slotLabel={`slot ${activeSlot + 1}`}
                regulationId={regulationId}
              />
            ) : (
              <EmptySlot slot={activeSlot} onPick={() => setPickerForSlot(activeSlot)} />
            )}
          </div>

          {/* Decision rail — damage vs top 5 meta + speed tier */}
          <div>
            <ContextRail member={activeMember} regulationId={regulationId} />
          </div>
        </div>
      )}

      <SpeciesPicker
        open={pickerForSlot !== null}
        onClose={() => setPickerForSlot(null)}
        onPick={(s) => pickerForSlot !== null && onPickSpecies(pickerForSlot, s)}
        currentSpecies={pickerForSlot !== null ? team[pickerForSlot]?.species : undefined}
        regulationId={regulationId}
      />
    </div>
  );
}

function TeamSlot({
  slot,
  member,
  active,
  onSelect,
  onClear,
  onPick,
}: {
  slot: number;
  member: EditorMember | null;
  active: boolean;
  onSelect: () => void;
  onClear: () => void;
  onPick: () => void;
}) {
  const evTotal = totalEvs(member?.evs);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '14px 36px 1fr 18px',
        gap: 8,
        alignItems: 'center',
        padding: '6px 8px',
        borderRadius: 3,
        background: active ? 'var(--accent-bg)' : 'transparent',
        borderLeft: `2px solid ${active ? 'var(--accent-line)' : 'transparent'}`,
        cursor: 'pointer',
      }}
      onClick={onSelect}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect()}
      // biome-ignore lint/a11y/useSemanticElements: a <button> would forbid the nested clear-× button; this row contains action children so we keep div + role
      role="button"
      tabIndex={0}
    >
      <span className="mono" style={{ fontSize: 9, color: 'var(--fg-4)' }}>
        {slot + 1}
      </span>
      {member ? (
        <>
          <Sprite species={member.species as SpeciesId} size={32} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <span
              style={{
                fontSize: 12,
                color: 'var(--fg-1)',
                fontWeight: active ? 600 : 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {member.species}
            </span>
            <span
              className="mono"
              style={{ fontSize: 10, color: 'var(--fg-3)', whiteSpace: 'nowrap' }}
            >
              {member.item ? `@ ${member.item}` : `${evTotal}/510 EVs`}
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="mono"
            style={{
              fontSize: 11,
              color: 'var(--fg-4)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            title="Clear slot"
          >
            ×
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPick();
            }}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: '1px dashed var(--line-2)',
              background: 'transparent',
              color: 'var(--fg-4)',
              fontSize: 14,
              cursor: 'pointer',
            }}
            title="Pick a Pokémon"
          >
            +
          </button>
          <span
            className="caps mono"
            style={{
              fontSize: 9,
              color: 'var(--fg-4)',
              letterSpacing: '0.06em',
            }}
          >
            empty
          </span>
          <span />
        </>
      )}
    </div>
  );
}

function EmptySlot({ slot, onPick }: { slot: number; onPick: () => void }) {
  return (
    <div
      className="vb-card"
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: '1px dashed var(--line-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--fg-4)',
          fontSize: 22,
        }}
      >
        +
      </div>
      <div>
        <div
          className="caps mono"
          style={{ fontSize: 10, color: 'var(--accent-2)', letterSpacing: '0.1em' }}
        >
          pick a pokémon
        </div>
        <div style={{ fontSize: 16, color: 'var(--fg-1)', marginTop: 4, fontWeight: 600 }}>
          Slot {slot + 1} is open.
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 6 }}>
          Search Pokémon and we'll instantiate a fresh slot with sensible defaults.
        </div>
      </div>
      <button
        type="button"
        onClick={onPick}
        className="mono"
        style={{
          fontSize: 12,
          padding: '7px 22px',
          background: 'var(--accent-bg)',
          color: 'var(--accent-2)',
          border: '1px solid var(--accent-line)',
          borderRadius: 3,
          cursor: 'pointer',
        }}
      >
        Search Pokémon…
      </button>
    </div>
  );
}

// Horizontal scrolling chip strip for mobile. Tap a chip to switch active
// slot; long-press / × not provided (mobile users edit one mon at a time
// and rarely need to clear from this strip — the editor header has a clear
// button).
function MobileTeamStrip({
  team,
  activeSlot,
  onSelect,
  onPick,
  onClear,
}: {
  team: Array<EditorMember | null>;
  activeSlot: number;
  onSelect: (i: number) => void;
  onPick: (i: number) => void;
  onClear: (i: number) => void;
}) {
  return (
    <div
      className="vb-card"
      style={{
        display: 'flex',
        gap: 6,
        padding: 6,
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
      }}
    >
      {team.map((m, i) => {
        const active = i === activeSlot;
        return (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: slot index IS the identity
            key={i}
            type="button"
            onClick={() => (m ? onSelect(i) : onPick(i))}
            onContextMenu={(e) => {
              if (m) {
                e.preventDefault();
                onClear(i);
              }
            }}
            style={{
              flex: '0 0 auto',
              scrollSnapAlign: 'start',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              minWidth: 56,
              padding: '4px 6px',
              background: active ? 'var(--accent-bg)' : 'transparent',
              border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line-1)'}`,
              borderRadius: 4,
              cursor: 'pointer',
            }}
            title={m ? `Slot ${i + 1}: ${m.species}` : `Slot ${i + 1}: empty`}
          >
            {m ? (
              <Sprite species={m.species as SpeciesId} size={36} />
            ) : (
              <span
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed var(--line-2)',
                  borderRadius: '50%',
                  color: 'var(--fg-4)',
                  fontSize: 16,
                }}
              >
                +
              </span>
            )}
            <span
              className="mono"
              style={{
                fontSize: 8.5,
                color: active ? 'var(--accent-2)' : 'var(--fg-4)',
                letterSpacing: '0.04em',
              }}
            >
              {i + 1}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Generic collapsible section — header strip + animated open/close. The
// header is a button so the whole bar is tap-target sized. Used by the
// builder rail on mobile to keep the first paint short.
function CollapsibleSection({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="vb-card" style={{ overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onToggle}
        className="mono caps"
        style={{
          width: '100%',
          padding: '10px 14px',
          background: 'transparent',
          color: 'var(--fg-3)',
          border: 'none',
          textAlign: 'left',
          fontSize: 10,
          letterSpacing: '0.08em',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: 11 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: 10, borderTop: '1px solid var(--line-1)' }}>{children}</div>}
    </div>
  );
}

function derivedName(members: EditorMember[]): string {
  return (
    members
      .slice(0, 3)
      .map((m) => {
        const base = (m.species ?? '').split('-')[0];
        return base.charAt(0).toUpperCase() + base.slice(1);
      })
      .join(' / ') || 'Untitled team'
  );
}
